import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileBarChart,
  Mail,
  Percent,
  Play,
  Send,
  X,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/use-auth';
import { PreviewModal } from '../components/PreviewModal';
import { SendReportModal } from '../components/SendReportModal';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  type ReportConfig,
  type ReportRun,
  type ReportsSummary,
} from '../lib/api';
import { deriveStatus, DISPLAY_LABEL, DISPLAY_VARIANT } from '../lib/reports';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Pagination } from '../components/ui/Pagination';
import { Select } from '../components/ui/Select';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelative,
} from '../lib/format';
import { cn } from '../lib/utils';


type RunRange = 30 | 90 | 180 | 'all';

const RUN_RANGES: { value: RunRange; label: string }[] = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 180 days' },
  { value: 'all', label: 'All time' },
];

const RUNS_PAGE_SIZE = 25;

const rangeLabel = (range: RunRange): string =>
  RUN_RANGES.find((r) => r.value === range)?.label.toLowerCase() ?? '';

const getRunValue = (item: ReportRun, key: string): unknown => {
  switch (key) {
    case 'report_id':
      return item.report_id;
    case 'report_date':
      return item.report_date ?? '';
    case 'status':
      return deriveStatus(item);
    case 'row_count':
      return item.row_count ?? 0;
    case 'duration_ms':
      return item.duration_ms ?? 0;
    case 'triggered_by':
      return item.triggered_by ?? '';
    case 'run_timestamp':
      return item.run_timestamp;
    default:
      return undefined;
  }
};

const getConfigValue = (
  item: ReportConfig & { lastRun: ReportRun | undefined },
  key: string,
): unknown => {
  switch (key) {
    case 'report_id':
      return item.report_id;
    case 'report_name':
      return item.report_name ?? '';
    case 'client':
      return item.client ?? '';
    case 'enabled':
      return item.enabled === true ? 1 : 0;
    case 'schedule':
      return item.schedule ?? '';
    case 'recipients':
      return item.recipients?.length ?? 0;
    case 'lastRun':
      return item.lastRun?.run_timestamp ?? '';
    case 'lastStatus':
      return item.lastRun ? deriveStatus(item.lastRun) : '';
    default:
      return undefined;
  }
};

export function ReportsMonitor() {
  const summary = useQuery({
    queryKey: ['reports', 'summary'],
    queryFn: () => api.getReportsSummary(),
    staleTime: 60 * 1000,
  });

  const [range, setRange] = useState<RunRange>(30);

  const runs = useQuery({
    queryKey: ['reports', 'runs', range],
    queryFn: () => api.listReportRuns({ days: range }),
    staleTime: 60 * 1000,
  });

  const configs = useQuery({
    queryKey: ['reports', 'configs'],
    queryFn: () => api.listReportConfigs(),
    staleTime: 5 * 60 * 1000,
  });

  const [runQuery, setRunQuery] = useState('');
  const [runPage, setRunPage] = useState(1);
  const [configQuery, setConfigQuery] = useState('');
  const [selected, setSelected] = useState<{ reportId: string; runTimestamp: string } | null>(null);

  // Memoised because the `?? []` otherwise mints a new array identity on every
  // render, which would re-run every downstream useMemo that depends on it.
  const allRuns = useMemo(() => runs.data?.items ?? [], [runs.data]);

  const filteredRuns = useMemo(() => {
    if (!runQuery.trim()) return allRuns;
    const needle = runQuery.toLowerCase();
    return allRuns.filter(
      (r) =>
        r.report_id.toLowerCase().includes(needle) ||
        deriveStatus(r).toLowerCase().includes(needle) ||
        r.report_date?.toLowerCase().includes(needle) ||
        r.triggered_by?.toLowerCase().includes(needle) ||
        r.error?.toLowerCase().includes(needle),
    );
  }, [allRuns, runQuery]);

  const { sortedItems: sortedRuns, sort: runSort, toggle: toggleRunSort } = useSort(
    filteredRuns,
    getRunValue,
  );

  // Clamped on render rather than reset in an effect, so a shrinking result set
  // never renders an empty page first.
  const runPageCount = Math.max(1, Math.ceil(sortedRuns.length / RUNS_PAGE_SIZE));
  const currentRunPage = Math.min(runPage, runPageCount);
  const pagedRuns = useMemo(
    () => sortedRuns.slice((currentRunPage - 1) * RUNS_PAGE_SIZE, currentRunPage * RUNS_PAGE_SIZE),
    [sortedRuns, currentRunPage],
  );

  const changeRunSort = (key: string) => {
    setRunPage(1);
    toggleRunSort(key);
  };

  const lastRunByReport = useMemo(() => {
    const map = new Map<string, ReportRun>();
    for (const r of allRuns) {
      const prev = map.get(r.report_id);
      if (!prev || prev.run_timestamp < r.run_timestamp) map.set(r.report_id, r);
    }
    return map;
  }, [allRuns]);

  const enrichedConfigs = useMemo(
    () =>
      (configs.data?.items ?? []).map((c) => ({
        ...c,
        lastRun: lastRunByReport.get(c.report_id),
      })),
    [configs.data, lastRunByReport],
  );

  const filteredConfigs = useMemo(() => {
    if (!configQuery.trim()) return enrichedConfigs;
    const needle = configQuery.toLowerCase();
    return enrichedConfigs.filter(
      (c) =>
        c.report_id.toLowerCase().includes(needle) ||
        c.report_name?.toLowerCase().includes(needle) ||
        c.client?.toLowerCase().includes(needle) ||
        c.schedule?.toLowerCase().includes(needle),
    );
  }, [enrichedConfigs, configQuery]);

  const {
    sortedItems: sortedConfigs,
    sort: configSort,
    toggle: toggleConfigSort,
  } = useSort(filteredConfigs, getConfigValue);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground">
          Scheduled Athena queries emailed as styled HTML + CSV. Runs every morning at 5 AM PT.
        </p>
      </div>

      <SummaryStrip summary={summary.data} loading={summary.isLoading} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">Throughput · last 14 days</CardTitle>
          <span className="text-xs text-muted-foreground">success · empty · failed per day</span>
        </CardHeader>
        <CardContent>
          <ThroughputChart daily={summary.data?.daily ?? []} loading={summary.isLoading} />
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-3 gap-3 flex-wrap">
          <CardTitle className="text-base">Recent runs</CardTitle>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {runs.isLoading
                ? 'Loading…'
                : runQuery.trim()
                  ? `${filteredRuns.length} of ${allRuns.length} runs`
                  : `${allRuns.length} runs`}
            </span>
            <Select
              value={String(range)}
              onChange={(e) => {
                setRange(e.target.value === 'all' ? 'all' : (Number(e.target.value) as RunRange));
                setRunPage(1);
              }}
              className="w-40"
              aria-label="Run history window"
            >
              {RUN_RANGES.map((r) => (
                <option key={r.value} value={String(r.value)}>
                  {r.label}
                </option>
              ))}
            </Select>
            <Input
              value={runQuery}
              onChange={(e) => {
                setRunQuery(e.target.value);
                setRunPage(1);
              }}
              placeholder="Search…"
              className="w-56"
            />
          </div>
        </CardHeader>
        {runs.error && (
          <div className="px-6 pb-3 text-sm text-destructive">{(runs.error as Error).message}</div>
        )}
        {runs.data?.truncated && (
          <div className="px-6 pb-3 text-xs text-warning">
            Hit the query ceiling for this window — older runs are missing. Narrow the range to see
            a complete history.
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-y border-border">
              <tr>
                <SortableHeader
                  label="Report"
                  sortKey="report_id"
                  sort={runSort}
                  onToggle={changeRunSort}
                />
                <SortableHeader
                  label="Target date"
                  sortKey="report_date"
                  sort={runSort}
                  onToggle={changeRunSort}
                />
                <SortableHeader
                  label="Status"
                  sortKey="status"
                  sort={runSort}
                  onToggle={changeRunSort}
                />
                <SortableHeader
                  label="Rows"
                  sortKey="row_count"
                  sort={runSort}
                  onToggle={changeRunSort}
                />
                <SortableHeader
                  label="Duration"
                  sortKey="duration_ms"
                  sort={runSort}
                  onToggle={changeRunSort}
                />
                <SortableHeader
                  label="Trigger"
                  sortKey="triggered_by"
                  sort={runSort}
                  onToggle={changeRunSort}
                />
                <SortableHeader
                  label="When"
                  sortKey="run_timestamp"
                  sort={runSort}
                  onToggle={changeRunSort}
                />
              </tr>
            </thead>
            <tbody>
              {runs.isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {pagedRuns.map((r) => {
                const ds = deriveStatus(r);
                const empty = ds === 'EMPTY';
                return (
                <tr
                  key={`${r.report_id}|${r.run_timestamp}`}
                  onClick={() =>
                    setSelected({ reportId: r.report_id, runTimestamp: r.run_timestamp })
                  }
                  className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3 font-medium">{r.report_id}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {r.report_date ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={DISPLAY_VARIANT[ds]}>{DISPLAY_LABEL[ds]}</Badge>
                  </td>
                  <td
                    className={cn(
                      'px-4 py-3 tabular-nums inline-flex items-center gap-1',
                      empty && 'text-warning font-medium',
                    )}
                  >
                    {empty && <AlertTriangle className="h-3.5 w-3.5" />}
                    {formatNumber(r.row_count)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">
                    {r.duration_ms == null ? '—' : `${(r.duration_ms / 1000).toFixed(1)}s`}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">
                    {r.triggered_by ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatRelative(r.run_timestamp)}
                  </td>
                </tr>
                );
              })}
              {!runs.isLoading && sortedRuns.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    {runQuery.trim()
                      ? 'No matching runs.'
                      : `No runs in the ${rangeLabel(range)} window.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!runs.isLoading && sortedRuns.length > 0 && (
          <Pagination
            page={currentRunPage}
            pageCount={runPageCount}
            pageSize={RUNS_PAGE_SIZE}
            total={sortedRuns.length}
            onPageChange={setRunPage}
          />
        )}
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-3 gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base">Configured reports</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Last run / last status come from the {rangeLabel(range)} window selected above.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {configs.isLoading
                ? 'Loading…'
                : `${filteredConfigs.length} of ${enrichedConfigs.length} reports`}
            </span>
            <Input
              value={configQuery}
              onChange={(e) => setConfigQuery(e.target.value)}
              placeholder="Search…"
              className="w-56"
            />
          </div>
        </CardHeader>
        {configs.error && (
          <div className="px-6 pb-3 text-sm text-destructive">
            {(configs.error as Error).message}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-y border-border">
              <tr>
                <SortableHeader
                  label="Report ID"
                  sortKey="report_id"
                  sort={configSort}
                  onToggle={toggleConfigSort}
                />
                <SortableHeader
                  label="Name"
                  sortKey="report_name"
                  sort={configSort}
                  onToggle={toggleConfigSort}
                />
                <SortableHeader
                  label="Client"
                  sortKey="client"
                  sort={configSort}
                  onToggle={toggleConfigSort}
                />
                <SortableHeader
                  label="Enabled"
                  sortKey="enabled"
                  sort={configSort}
                  onToggle={toggleConfigSort}
                />
                <SortableHeader
                  label="Schedule"
                  sortKey="schedule"
                  sort={configSort}
                  onToggle={toggleConfigSort}
                />
                <SortableHeader
                  label="Recipients"
                  sortKey="recipients"
                  sort={configSort}
                  onToggle={toggleConfigSort}
                />
                <SortableHeader
                  label="Last run"
                  sortKey="lastRun"
                  sort={configSort}
                  onToggle={toggleConfigSort}
                />
                <SortableHeader
                  label="Last status"
                  sortKey="lastStatus"
                  sort={configSort}
                  onToggle={toggleConfigSort}
                />
              </tr>
            </thead>
            <tbody>
              {configs.isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {sortedConfigs.map((c) => (
                <tr
                  key={c.report_id}
                  onClick={() =>
                    c.lastRun &&
                    setSelected({
                      reportId: c.lastRun.report_id,
                      runTimestamp: c.lastRun.run_timestamp,
                    })
                  }
                  className={cn(
                    'border-b border-border last:border-0 transition-colors',
                    c.lastRun && 'hover:bg-muted/50 cursor-pointer',
                  )}
                >
                  <td className="px-4 py-3 font-medium">{c.report_id}</td>
                  <td className="px-4 py-3">{c.report_name ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.client ?? '—'}</td>
                  <td className="px-4 py-3">
                    {c.enabled === true ? (
                      <Badge variant="success">Yes</Badge>
                    ) : c.enabled === false ? (
                      <Badge variant="secondary">No</Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {c.schedule ?? '* * *'}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">
                    {c.recipients?.length ?? 0}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.lastRun ? formatRelative(c.lastRun.run_timestamp) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {c.lastRun ? (
                      (() => {
                        const ds = deriveStatus(c.lastRun);
                        return <Badge variant={DISPLAY_VARIANT[ds]}>{DISPLAY_LABEL[ds]}</Badge>;
                      })()
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
              {!configs.isLoading && sortedConfigs.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">
                    No matching reports.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {selected && (
        <RunDetailDrawer
          reportId={selected.reportId}
          runTimestamp={selected.runTimestamp}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function SummaryStrip({
  summary,
  loading,
}: {
  summary: ReportsSummary | undefined;
  loading: boolean;
}) {
  const successRate =
    summary?.successRate7d == null ? null : (summary.successRate7d * 100).toFixed(1);
  const total7d = (summary?.success7d ?? 0) + (summary?.failed7d ?? 0);

  return (
    <div className="grid gap-4 md:grid-cols-4">
      <StatCard
        title="Last run"
        icon={<Clock className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      >
        {summary?.latest ? (
          (() => {
            const ds = deriveStatus(summary.latest);
            return (
              <>
                <div className="flex items-center gap-2">
                  <Badge variant={DISPLAY_VARIANT[ds]}>{DISPLAY_LABEL[ds]}</Badge>
                  <span className="text-sm text-muted-foreground">
                    {summary.latest.report_id}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatRelative(summary.latest.run_timestamp)}
                </p>
              </>
            );
          })()
        ) : (
          <span className="text-muted-foreground">No runs yet</span>
        )}
      </StatCard>

      <StatCard
        title="Failures (24h)"
        icon={<AlertCircle className="h-4 w-4 text-destructive" />}
        loading={loading}
      >
        <div
          className={cn(
            'text-2xl font-bold tabular-nums',
            summary?.failures24h && summary.failures24h > 0
              ? 'text-destructive'
              : 'text-foreground',
          )}
        >
          {formatNumber(summary?.failures24h ?? 0)}
        </div>
        {summary?.empty24h != null && summary.empty24h > 0 && (
          <p className="text-xs text-warning mt-1 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {summary.empty24h} empty result{summary.empty24h === 1 ? '' : 's'}
          </p>
        )}
      </StatCard>

      <StatCard
        title="Success rate (7d)"
        icon={<Percent className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      >
        <div className="text-2xl font-bold tabular-nums">
          {successRate == null ? '—' : `${successRate}%`}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {total7d === 0 ? 'no runs' : `${summary?.success7d}/${total7d} succeeded`}
        </p>
      </StatCard>

      <StatCard
        title="Configured reports"
        icon={<FileBarChart className="h-4 w-4 text-muted-foreground" />}
        loading={loading}
      >
        <div className="text-2xl font-bold tabular-nums">
          {formatNumber(summary?.enabledCount ?? 0)}
          <span className="text-muted-foreground text-base font-medium">
            {' '}
            / {formatNumber(summary?.configuredCount ?? 0)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">enabled / total</p>
      </StatCard>
    </div>
  );
}

function StatCard({
  title,
  icon,
  children,
  loading,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {loading ? <span className="text-muted-foreground">…</span> : children}
      </CardContent>
    </Card>
  );
}

function ThroughputChart({
  daily,
  loading,
}: {
  daily: { date: string; succeeded: number; empty: number; failed: number }[];
  loading: boolean;
}) {
  const data = useMemo(
    () =>
      daily.map((d) => ({
        date: new Date(d.date).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
        }),
        succeeded: d.succeeded,
        empty: d.empty,
        failed: d.failed,
      })),
    [daily],
  );

  if (loading) {
    return (
      <div className="h-56 flex items-center justify-center text-muted-foreground">
        Loading chart…
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <div className="h-56 flex items-center justify-center text-muted-foreground">
        No runs in the last 14 days.
      </div>
    );
  }
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
            width={32}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-muted)' }}
            contentStyle={{
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--color-foreground)',
            }}
          />
          <Bar dataKey="succeeded" stackId="a" fill="var(--color-success)" />
          <Bar dataKey="empty" stackId="a" fill="var(--color-warning)" />
          <Bar dataKey="failed" stackId="a" fill="var(--color-destructive)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RunDetailDrawer({
  reportId,
  runTimestamp,
  onClose,
}: {
  reportId: string;
  runTimestamp: string;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const canPreview = can('reporting.preview');
  const canRun = can('reporting.run');

  const run = useQuery({
    queryKey: ['reports', 'run', reportId, runTimestamp],
    queryFn: () => api.getReportRun(reportId, runTimestamp),
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-card border-l border-border shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold">{reportId}</h2>
            <p className="text-xs text-muted-foreground">{formatDateTime(runTimestamp)}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-5">
          {run.isLoading && <p className="text-muted-foreground">Loading…</p>}
          {run.error && <p className="text-destructive">{(run.error as Error).message}</p>}
          {run.data && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                {(() => {
                  const ds = deriveStatus(run.data);
                  return <Badge variant={DISPLAY_VARIANT[ds]}>{DISPLAY_LABEL[ds]}</Badge>;
                })()}
                {run.data.triggered_by && (
                  <Badge variant="outline" className="capitalize">
                    {run.data.triggered_by}
                  </Badge>
                )}
                {run.data.report_date && (
                  <span className="text-sm text-muted-foreground">
                    target {run.data.report_date}
                  </span>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                {canPreview && run.data.report_date && (
                  <Button variant="outline" onClick={() => setPreviewOpen(true)}>
                    <Play className="h-4 w-4" />
                    Preview data
                  </Button>
                )}
                {canRun && run.data.report_date && (
                  <Button variant="outline" onClick={() => setSendOpen(true)}>
                    <Send className="h-4 w-4" />
                    Re-run + send
                  </Button>
                )}
              </div>

              {deriveStatus(run.data) === 'EMPTY' && (
                <div className="rounded-md border border-warning/40 bg-warning/10 text-warning px-4 py-3 text-sm flex gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Query returned 0 rows.</p>
                    <p className="text-xs mt-1 opacity-90">
                      The Athena query ran successfully but produced no results. If data has
                      backfilled since, click <span className="font-medium">Preview data</span> to
                      check, then <span className="font-medium">Re-run + send</span> to resend the
                      email.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Tile
                  label="Rows returned"
                  value={formatNumber(run.data.row_count)}
                  icon={<FileBarChart className="h-4 w-4 text-muted-foreground" />}
                />
                <Tile
                  label="Duration"
                  value={
                    run.data.duration_ms == null
                      ? '—'
                      : formatDuration(
                          new Date(
                            new Date(run.data.run_timestamp).getTime() - run.data.duration_ms,
                          ).toISOString(),
                          run.data.run_timestamp,
                        )
                  }
                  icon={<Clock className="h-4 w-4 text-muted-foreground" />}
                />
                <Tile
                  label="Recipients"
                  value={formatNumber(run.data.recipients?.length)}
                  icon={<Mail className="h-4 w-4 text-muted-foreground" />}
                />
              </div>

              <section>
                <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Recipients ({run.data.recipients?.length ?? 0})
                </h3>
                <Card>
                  <CardContent className="pt-6">
                    {run.data.recipients && run.data.recipients.length > 0 ? (
                      <ul className="space-y-1 text-sm">
                        {run.data.recipients.map((r) => (
                          <li key={r} className="font-mono text-xs">
                            {r}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No recipients recorded for this run.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </section>

              {run.data.error && (
                <section>
                  <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-destructive" />
                    Error
                  </h3>
                  <Card className="border-destructive/40">
                    <CardContent className="pt-6">
                      <pre className="text-xs text-destructive whitespace-pre-wrap font-mono">
                        {run.data.error}
                      </pre>
                    </CardContent>
                  </Card>
                </section>
              )}

              {deriveStatus(run.data) === 'SUCCESS' && !run.data.error && (
                <div className="flex items-center gap-2 text-sm text-success">
                  <CheckCircle2 className="h-4 w-4" />
                  Email delivered successfully.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {previewOpen && run.data?.report_date && (
        <PreviewModal
          reportId={reportId}
          reportName={reportId}
          initialDate={run.data.report_date}
          autoRun
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {sendOpen && run.data?.report_date && (
        <SendReportModal
          reportId={reportId}
          reportName={reportId}
          date={run.data.report_date}
          onClose={() => setSendOpen(false)}
        />
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-xl font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}
